import React, { useState, useEffect } from 'react';
import Card, { CardBody, CardHeader } from '../../../components/bootstrap/Card';
import Button from '../../../components/bootstrap/Button';
import Input from '../../../components/bootstrap/forms/Input';
import Select from '../../../components/bootstrap/forms/Select';
import Spinner from '../../../components/bootstrap/Spinner';
import Badge from '../../../components/bootstrap/Badge';
import PaginationButtons from '../../../components/PaginationButtons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { apiClient } from '../../../shared/base-classes/apiBaseClass';
import ascendingIcon from '../../../sorting/ascending-sorting.png';
import descendingIcon from '../../../sorting/descending-sorting.png';

interface PdfRequestData {
	id: number;
	ip: string;
	targetUrl: string;
	status: string;
	source: string;
	type: string;
	createdAt: string;
	updatedAt: string;
	userId: number | null;
	country: string;
	countryCode: string;
	region: string;
	city: string;
	isp: string;
}

interface ApiResponse {
	data: {
		data: PdfRequestData[];
		statistics: {
			totalRequests: number;
			uniqueIPs: number;
			avgRequestsPerIP: number;
			completionRate: number;
		};
		per_page: number;
		total_records: number;
		current_page: number;
		next_page: number | null;
		previous_page: number | null;
		uniqueCountries: number;
		period: {
			start: string;
			end: string;
		};
	};
	errors: any[];
	message: string;
	status: boolean;
}

interface PaginationState {
	current_page: number;
	total_records: number;
	per_page: number;
	next_page: number | null;
	previous_page: number | null;
	uniqueCountries: number;
}

const PdfRequestReport = () => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { t } = useTranslation();
	const [data, setData] = useState<PdfRequestData[]>([]);
	const [loading, setLoading] = useState(false);
	const [sortField, setSortField] = useState<string>('createdAt');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
	const [statistics, setStatistics] = useState({
		totalRequests: 0,
		uniqueIPs: 0,
		avgRequestsPerIP: 0,
		completionRate: 0
	});
	const [pagination, setPagination] = useState<PaginationState>({
		current_page: 1,
		total_records: 0,
		per_page: 10,
		next_page: null,
		previous_page: null,
		uniqueCountries: 0
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [filters, setFilters] = useState({
		search: '',
		startDate: '',
		endDate: '',
		per_page: 10
	});

	const fetchData = async () => {
		setLoading(true);
		try {
		const params = {
			per_page: perPage,
			page: currentPage,
			search: filters.search,
			startDate: filters.startDate,
			endDate: filters.endDate,
			order_by_field: sortField,
			order_by: sortOrder
		};

			const result: ApiResponse = await apiClient.get('admin/analytics/requests-with-countries', { params });

			if (result.status) {
				setData(result.data.data);
				setStatistics(result.data.statistics);
				setPagination({
					current_page: result.data.current_page,
					total_records: result.data.total_records,
					per_page: result.data.per_page,
					next_page: result.data.next_page,
					previous_page: result.data.previous_page,
					uniqueCountries: result.data.uniqueCountries
				});
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, perPage, filters, sortField, sortOrder]);

	const handleFilterChange = (field: string, value: any) => {
		setFilters(prev => ({ ...prev, [field]: value }));
		setCurrentPage(1);
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const toggleSortOrder = (field: string) => {
		if (sortField === field) {
			setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			setSortOrder('desc');
		}
	};

	const handleClearFilters = () => {
		setFilters({
			search: '',
			startDate: '',
			endDate: '',
			per_page: 10
		});
		setCurrentPage(1);
	};

	const getStatusBadge = (status: string) => {
		const statusConfig = {
			complete: { color: 'success' as const, text: 'Complete' },
			processing: { color: 'warning' as const, text: 'Processing' },
			failed: { color: 'danger' as const, text: 'Failed' },
			pending: { color: 'info' as const, text: 'Pending' }
		};
		
		const config = statusConfig[status as keyof typeof statusConfig] || { color: 'secondary' as const, text: status };
		return <Badge color={config.color}>{config.text}</Badge>;
	};

	const formatDate = (dateString: string) => {
		return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
	};

	return (
		<div>
			<Card>
				<CardHeader>
					<h5>PDF Request Analytics</h5>
					<p className="text-muted">Detailed report of PDF generation requests with country data</p>
				</CardHeader>
				<CardBody className="table-responsive">
					{/* Filters */}
					<div className="row mb-4">
						<div className="col-md-3">
							<Input
								type="text"
								placeholder="Search..."
								value={filters.search}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('search', e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<Input
								type="date"
								placeholder="Start Date"
								value={filters.startDate}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('startDate', e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<Input
								type="date"
								placeholder="End Date"
								value={filters.endDate}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('endDate', e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<Select
								ariaLabel="Items per page"
								value={filters.per_page.toString()}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('per_page', parseInt(e.target.value))}>
								<option value={10}>10</option>
								<option value={25}>25</option>
								<option value={50}>50</option>
								<option value={100}>100</option>
							</Select>
						</div>
						<div className="col-md-3 d-flex gap-2 justify-content-end">
							<Button color="primary" onClick={fetchData}>
								Filter
							</Button>
							<Button color="secondary" onClick={handleClearFilters}>
								Clear
							</Button>
						</div>
					</div>

					{/* Summary Cards */}
					<div className="row mb-4">
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.totalRequests}</h3>
									<p className="text-white-50 mb-0">Total Requests</p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.uniqueIPs}</h3>
									<p className="text-white-50 mb-0">Unique IPs</p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.avgRequestsPerIP.toFixed(1)}</h3>
									<p className="text-white-50 mb-0">Avg. Requests per IP</p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.completionRate.toFixed(1)}%</h3>
									<p className="text-white-50 mb-0">Completion Rate</p>
								</CardBody>
							</Card>
						</div>
					</div>

					{/* Data Table */}
					{loading ? (
						<div className="text-center py-4">
							<Spinner color="primary" />
							<p>Loading data...</p>
						</div>
					) : (
						<table className="table table-modern">
							<thead>
								<tr>
									<th onClick={() => toggleSortOrder('id')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											ID
											{sortField === 'id' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
									<th onClick={() => toggleSortOrder('targetUrl')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											Target URL
											{sortField === 'targetUrl' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
									<th onClick={() => toggleSortOrder('status')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											Status
											{sortField === 'status' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
									<th>Country</th>
									<th>City</th>
									<th onClick={() => toggleSortOrder('ip')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											IP Address
											{sortField === 'ip' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
									<th onClick={() => toggleSortOrder('createdAt')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											Created At
											{sortField === 'createdAt' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
								</tr>
							</thead>
							<tbody>
								{data.map((item) => (
									<tr key={item.id}>
										<td>{item.id}</td>
										<td>
											<a 
												href={item.targetUrl} 
												target="_blank" 
												rel="noopener noreferrer"
												className="text-decoration-none"
											>
												{item.targetUrl.length > 50 
													? `${item.targetUrl.substring(0, 50)}...` 
													: item.targetUrl
												}
											</a>
										</td>
										<td>{getStatusBadge(item.status)}</td>
										<td>
											<span className="fw-bold">{item.country}</span>
											<br />
											<small className="text-muted">{item.countryCode}</small>
										</td>
										<td>{item.city}</td>
										<td>{item.ip}</td>
										<td>{formatDate(item.createdAt)}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}

					<PaginationButtons
						label="entries"
						setCurrentPage={setCurrentPage}
						currentPage={currentPage}
						perPage={perPage}
						setPerPage={setPerPage}
						totalItems={pagination.total_records}
					/>
				</CardBody>
			</Card>
		</div>
	);
};

export default PdfRequestReport;
